import {
	Directive,
	ElementRef,
	Input,
	Renderer2,
	TemplateRef,
	ViewContainerRef,
} from '@angular/core';

// --- Facades ---
import { ParametersFacade } from '@facades/parameters.facade';
import { TenantsFacade } from '@facades/tenants.facade';

// --- Interfaces ---
import type { IProduct } from '@interfaces/product';

// --- Utils ---
import {
	getDefaultStoreId,
	isProductOutOfStock,
	isProductSellable,
} from '@utils/stock.util';

interface IElementProps {
	product: IProduct;
	type?: string;
	component?: string;
}

@Directive({
	selector: '[appProductAvailability]',
})
export class ProductAvailabilityDirective {
	constructor(
		private _el: ElementRef,
		private _renderer: Renderer2,
		private _templateRef: TemplateRef<any>,
		private _viewContainer: ViewContainerRef,
		private parametersFacade: ParametersFacade,
		private tenantsFacade: TenantsFacade
	) {}

	get parameters() {
		return this.parametersFacade.parameters;
	}

	get tenant() {
		return this.tenantsFacade.tenant;
	}

	private get defaultStoreId() {
		return getDefaultStoreId(this.parameters);
	}

	@Input() set appProductAvailability(element: IElementProps) {
		this._viewContainer.clear();

		const isNotCounterType = !element.type || element.type !== 'counter';
		const isInactive = Boolean(element.product?.inactivatedAt);

		if (isInactive) {
			if (isNotCounterType) (this._el.nativeElement as HTMLElement).remove();
			else this.renderUnavailableFlag();
			return;
		}

		const allowSellingWithoutStock =
			!!this.parameters?.allowSellingWithoutStock;
		const allowSellingParentProduct =
			this.parameters?.allowSellingParentProduct || false;

		const enableVariations = this.tenant?.features?.variations || false;
		const hasVariations = Boolean(element.product?.variations?.length);
		const variationsEnabledAndPresent = enableVariations && hasVariations;

		const parentSellable = isProductSellable(
			element.product,
			false,
			this.defaultStoreId
		);
		const variationsHasStock = this.checkStockAvailability(element.product);
		const isStockEmpty = this.isStockEmpty(element.product);

		let isAvailable: boolean;
		if (variationsEnabledAndPresent) {
			if (allowSellingParentProduct) {
				isAvailable =
					allowSellingWithoutStock || parentSellable || variationsHasStock;
			} else {
				isAvailable = allowSellingWithoutStock || variationsHasStock;
			}
		} else {
			isAvailable = allowSellingWithoutStock || parentSellable;
		}

		const showFlag = () => {
			if (isStockEmpty && this.parameters?.showProductsWithoutStockAlert) {
				if (isNotCounterType) this.renderStockFlag(element);
			}
			if (isAvailable) {
				this._viewContainer.createEmbeddedView(this._templateRef);
			}
		};

		if (!variationsEnabledAndPresent) {
			showFlag();
			return;
		}

		if (allowSellingParentProduct) {
			if (element.component) {
				showFlag();
				return;
			}

			const shouldShowStockAlert =
				!variationsHasStock &&
				isStockEmpty &&
				this.parameters?.showProductsWithoutStockAlert;

			if (shouldShowStockAlert && isNotCounterType) {
				this.renderStockFlag(element);
			}

			if (isAvailable) {
				this._viewContainer.createEmbeddedView(this._templateRef);
			}

			return;
		}

		const needsStockWarning =
			!variationsHasStock && this.parameters?.showProductsWithoutStockAlert;

		if (!isAvailable && isNotCounterType && needsStockWarning) {
			this.renderStockFlag(element);
		}

		if (isAvailable) {
			this._viewContainer.createEmbeddedView(this._templateRef);
		}
	}

	private checkStockAvailability(product: IProduct) {
		return (
			product.variations?.some(variation =>
				isProductSellable(variation, false, this.defaultStoreId)
			) ?? false
		);
	}

	private isStockEmpty(product: IProduct): boolean {
		const parentOutOfStock = isProductOutOfStock(product, this.defaultStoreId);
		const variations = product.variations || [];

		if (!variations.length) return parentOutOfStock;

		const variationsOutOfStock = variations.every(variation =>
			isProductOutOfStock(variation, this.defaultStoreId)
		);

		return parentOutOfStock && variationsOutOfStock;
	}

	private renderStockFlag(element: IElementProps) {
		const paragraph = this._renderer.createElement('p');
		const stockMessage = !element.type ? 'Sem estoque' : 'S/Estoq.';
		const text = this._renderer.createText(stockMessage);
		const { nativeElement } = this._el;

		this._renderer.appendChild(paragraph, text);
		this._renderer.addClass(paragraph, 'unavailable-product');
		this._renderer.insertBefore(
			nativeElement.parentElement,
			paragraph,
			nativeElement
		);
	}

	private renderUnavailableFlag() {
		const paragraph = this._renderer.createElement('p');
		const text = this._renderer.createText('Indisponível');
		const { nativeElement } = this._el;

		this._renderer.appendChild(paragraph, text);
		this._renderer.addClass(paragraph, 'unavailable-product');
		this._renderer.insertBefore(
			nativeElement.parentElement,
			paragraph,
			nativeElement
		);
	}
}
